import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getSessionHeaders } from '../../lib/serverSession'

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface Beneficiary {
  id: number
  user_id: number
  full_name: string
  relationship: string
  date_of_birth: string | null
  id_number: string | null
  email: string | null
  phone: string | null
  address: string | null
  city: string | null
  country: string | null
  postal_code: string | null
  percentage_allocation: number
  is_primary: boolean
  is_active: boolean
  notes: string | null
  created_at: string
  updated_at: string
}

interface BeneficiaryFormData {
  full_name: string
  relationship: string
  date_of_birth: string
  id_number: string
  email: string
  phone: string
  address: string
  city: string
  country: string
  postal_code: string
  percentage_allocation: number
  is_primary: boolean
  notes: string
}

const RELATIONSHIP_OPTIONS = [
  'Spouse',
  'Child',
  'Parent',
  'Sibling',
  'Grandparent',
  'Grandchild',
  'Aunt/Uncle',
  'Niece/Nephew',
  'Cousin',
  'Friend',
  'Business Partner',
  'Legal Guardian',
  'Other'
]

const initialFormData: BeneficiaryFormData = {
  full_name: '',
  relationship: '',
  date_of_birth: '',
  id_number: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  country: '',
  postal_code: '',
  percentage_allocation: 100,
  is_primary: false,
  notes: ''
}

interface BeneficiaryManagerProps {
  userId: number
}

const BeneficiaryManager: React.FC<BeneficiaryManagerProps> = ({ userId }) => {
  const { t } = useTranslation('common')
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState<BeneficiaryFormData>(initialFormData)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

  useEffect(() => {
    loadBeneficiaries()
  }, [userId])

  const loadBeneficiaries = async () => {
    try {
      setLoading(true)

      const response = await fetch(`${API_BASE_URL}/api/user/beneficiary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, operation: 'load' })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)
      setBeneficiaries(result.data?.beneficiaries || [])
    } catch (error) {
      console.error('Error loading beneficiaries:', error)
      setMessage({ type: 'error', text: 'Failed to load beneficiaries' })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              name === 'percentage_allocation' ? parseFloat(value) || 0 : value
    }))
  }

  const validateForm = (): string | null => {
    if (!formData.full_name.trim()) return 'Full name is required'
    if (!formData.relationship) return 'Relationship is required'
    if (formData.percentage_allocation <= 0 || formData.percentage_allocation > 100) {
      return 'Percentage allocation must be between 1 and 100'
    }
    const totalAllocation = beneficiaries
      .filter(b => b.id !== editingId)
      .reduce((sum, b) => sum + b.percentage_allocation, 0) + formData.percentage_allocation
    if (totalAllocation > 100) {
      return `Total allocation cannot exceed 100%. Current total would be ${totalAllocation}%`
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationError = validateForm()
    if (validationError) {
      setMessage({ type: 'error', text: validationError })
      return
    }

    setSaving(true)
    setMessage(null)

    try {
      const response = await fetch(`${API_BASE_URL}/api/user/beneficiary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'save',
          data: {
            beneficiaryId: editingId,
            beneficiaryData: formData
          }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      if (editingId) {
        setMessage({ type: 'success', text: 'Beneficiary updated successfully' })
      } else {
        setMessage({ type: 'success', text: 'Beneficiary added successfully' })
      }

      resetForm()
      await loadBeneficiaries()
    } catch (error) {
      console.error('Error saving beneficiary:', error)
      setMessage({ type: 'error', text: 'Failed to save beneficiary' })
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (beneficiary: Beneficiary) => {
    setFormData({
      full_name: beneficiary.full_name,
      relationship: beneficiary.relationship,
      date_of_birth: beneficiary.date_of_birth || '',
      id_number: beneficiary.id_number || '',
      email: beneficiary.email || '',
      phone: beneficiary.phone || '',
      address: beneficiary.address || '',
      city: beneficiary.city || '',
      country: beneficiary.country || '',
      postal_code: beneficiary.postal_code || '',
      percentage_allocation: beneficiary.percentage_allocation,
      is_primary: beneficiary.is_primary,
      notes: beneficiary.notes || ''
    })
    setEditingId(beneficiary.id)
    setShowForm(true)
  }

  const handleDelete = async (id: number) => {
    try {
      setSaving(true)

      const response = await fetch(`${API_BASE_URL}/api/user/beneficiary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'delete',
          data: { beneficiaryId: id }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      setMessage({ type: 'success', text: t('beneficiaries.toast.removed', 'Beneficiary removed successfully') })
      setDeleteConfirmId(null)
      await loadBeneficiaries()
    } catch (error) {
      console.error('Error deleting beneficiary:', error)
      setMessage({ type: 'error', text: t('beneficiaries.toast.removeFailed', 'Failed to remove beneficiary') })
    } finally {
      setSaving(false)
    }
  }

  const handleSetPrimary = async (id: number) => {
    try {
      setSaving(true)

      const response = await fetch(`${API_BASE_URL}/api/user/beneficiary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'set_primary',
          data: { beneficiaryId: id }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      setMessage({ type: 'success', text: t('beneficiaries.toast.primaryUpdated', 'Primary beneficiary updated') })
      await loadBeneficiaries()
    } catch (error) {
      console.error('Error setting primary beneficiary:', error)
      setMessage({ type: 'error', text: t('beneficiaries.toast.primaryFailed', 'Failed to update primary beneficiary') })
    } finally {
      setSaving(false)
    }
  }

  const resetForm = () => {
    setFormData(initialFormData)
    setEditingId(null)
    setShowForm(false)
  }

  const totalAllocation = beneficiaries.reduce((sum, b) => sum + b.percentage_allocation, 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        <span className="ml-3 text-white">{t('beneficiaries.loading', 'Loading beneficiaries...')}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">👨‍👩‍👧‍👦 {t('beneficiaries.heading', 'Beneficiaries / Next of Kin')}</h2>
          <p className="text-white mt-1">
            {t('beneficiaries.subheading', 'Designate who should receive your shares in case of unforeseen circumstances.')}
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
          >
            + {t('beneficiaries.addButton', 'Add Beneficiary')}
          </button>
        )}
      </div>

      {/* Message */}
      {message && (
        <div className={`rounded-lg p-4 ${
          message.type === 'success'
            ? 'bg-green-900/20 border border-green-500/30 text-green-300'
            : 'bg-red-900/20 border border-red-500/30 text-red-300'
        }`}>
          <p className="text-sm">{message.type === 'success' ? '✅' : '❌'} {message.text}</p>
        </div>
      )}

      {/* Allocation Summary */}
      {beneficiaries.length > 0 && (
        <div className="bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-300">{t('beneficiaries.totalAllocation', 'Total Allocation:')}</span>
            <span className={`font-bold ${totalAllocation === 100 ? 'text-green-400' : 'text-yellow-400'}`}>
              {totalAllocation}%
            </span>
          </div>
          {totalAllocation !== 100 && (
            <p className="text-yellow-400 text-sm mt-2">
              ⚠️ {t('beneficiaries.allocationWarning', 'Total allocation should equal 100% for complete coverage.')}
            </p>
          )}
        </div>
      )}

      {/* Beneficiary Form */}
      {showForm && (
        <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? `✏️ ${t('beneficiaries.editHeading', 'Edit Beneficiary')}` : `➕ ${t('beneficiaries.addNewHeading', 'Add New Beneficiary')}`}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.fullName', 'Full Name *')}</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                  required
                />
              </div>
              {/* Relationship */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.relationship', 'Relationship *')}</label>
                <select
                  name="relationship"
                  value={formData.relationship}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                  required
                >
                  <option value="">{t('beneficiaries.field.relationshipPlaceholder', 'Select relationship...')}</option>
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.dateOfBirth', 'Date of Birth')}</label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* ID Number */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.idNumber', 'ID/Passport Number')}</label>
                <input
                  type="text"
                  name="id_number"
                  value={formData.id_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.email', 'Email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.phone', 'Phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* Address */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.address', 'Address')}</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.city', 'City')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.country', 'Country')}</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* Postal Code */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.postalCode', 'Postal Code')}</label>
                <input
                  type="text"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                />
              </div>
              {/* Percentage Allocation */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.allocation', 'Allocation % *')}</label>
                <input
                  type="number"
                  name="percentage_allocation"
                  value={formData.percentage_allocation}
                  onChange={handleInputChange}
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                  required
                />
              </div>
            </div>
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">{t('beneficiaries.field.notes', 'Notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={2}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white resize-none"
              />
            </div>
            {/* Primary Checkbox */}
            <label className="flex items-center gap-2 text-gray-300">
              <input
                type="checkbox"
                name="is_primary"
                checked={formData.is_primary}
                onChange={handleInputChange}
                className="rounded border-gray-600 bg-gray-800"
              />
              {t('beneficiaries.field.setPrimary', 'Set as primary beneficiary')}
            </label>
            {/* Form Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={resetForm}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
                disabled={saving}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium"
                disabled={saving}
              >
                {saving ? t('common.saving', 'Saving...') : editingId ? t('beneficiaries.updateButton', 'Update Beneficiary') : t('beneficiaries.addButton', 'Add Beneficiary')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Beneficiary List */}
      {beneficiaries.length === 0 && !showForm ? (
        <div className="bg-gray-700 rounded-lg p-8 text-center">
          <div className="text-5xl mb-4">👨‍👩‍👧‍👦</div>
          <h3 className="text-lg font-semibold text-white mb-2">{t('beneficiaries.empty.heading', 'No Beneficiaries Added')}</h3>
          <p className="text-white mb-4">
            {t('beneficiaries.empty.body', 'Add beneficiaries to ensure your shares are transferred to your loved ones.')}
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium"
          >
            + {t('beneficiaries.empty.addFirst', 'Add Your First Beneficiary')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {beneficiaries.map(beneficiary => (
            <div
              key={beneficiary.id}
              className={`bg-gray-700 rounded-lg p-4 border ${
                beneficiary.is_primary ? 'border-yellow-500' : 'border-gray-600'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-lg font-semibold text-white">{beneficiary.full_name}</h4>
                    {beneficiary.is_primary && (
                      <span className="px-2 py-0.5 text-sm font-medium bg-yellow-600 text-white rounded">
                        {t('beneficiaries.primaryBadge', 'Primary')}
                      </span>
                    )}
                    <span className="px-2 py-0.5 text-sm font-medium bg-blue-600 text-white rounded">
                      {beneficiary.percentage_allocation}%
                    </span>
                  </div>
                  <p className="text-white text-sm">{beneficiary.relationship}</p>

                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    {beneficiary.email && (
                      <div className="text-gray-300">📧 {beneficiary.email}</div>
                    )}
                    {beneficiary.phone && (
                      <div className="text-gray-300">📱 {beneficiary.phone}</div>
                    )}
                    {beneficiary.id_number && (
                      <div className="text-gray-300">🪪 ID: {beneficiary.id_number}</div>
                    )}
                    {beneficiary.city && beneficiary.country && (
                      <div className="text-gray-300">📍 {beneficiary.city}, {beneficiary.country}</div>
                    )}
                  </div>

                  {beneficiary.notes && (
                    <p className="text-white text-sm mt-2 italic">"{beneficiary.notes}"</p>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {!beneficiary.is_primary && (
                    <button
                      onClick={() => handleSetPrimary(beneficiary.id)}
                      className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded"
                      disabled={saving}
                      title={t('beneficiaries.setPrimaryTitle', 'Set as primary')}
                    >
                      ⭐
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(beneficiary)}
                    className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded"
                    disabled={saving}
                  >
                    ✏️ {t('common.edit', 'Edit')}
                  </button>
                  {deleteConfirmId === beneficiary.id ? (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDelete(beneficiary.id)}
                        className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded"
                        disabled={saving}
                      >
                        {t('common.confirm', 'Confirm')}
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-500 text-white rounded"
                      >
                        {t('common.cancel', 'Cancel')}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(beneficiary.id)}
                      className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded"
                      disabled={saving}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
        <h4 className="text-blue-300 font-medium mb-2">ℹ️ {t('beneficiaries.info.heading', 'Important Information')}</h4>
        <ul className="text-blue-200 text-sm space-y-1">
          <li>• {t('beneficiaries.info.item1', 'Beneficiaries will receive your shares according to the allocation percentages.')}</li>
          <li>• {t('beneficiaries.info.item2', 'Total allocation should equal 100% for complete coverage.')}</li>
          <li>• {t('beneficiaries.info.item3', 'The primary beneficiary will be contacted first in case of any issues.')}</li>
          <li>• {t('beneficiaries.info.item4', 'Keep beneficiary information up to date for smooth transfer of assets.')}</li>
        </ul>
      </div>
    </div>
  )
}

export default BeneficiaryManager